import Logout from '@mui/icons-material/Logout';
import Settings from '@mui/icons-material/Settings';
import Person from '@mui/icons-material/Person';
import History from '@mui/icons-material/History';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { Moon, Sun, ChevronDown } from 'lucide-react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { useCompanyLogo } from '../../hooks/useCompanyLogo';
import { AccountCircle as AccountCircleIcon } from '@mui/icons-material';
import { LinearProgress } from '@mui/material';
import { usePermissions, SETTINGS_PERMISSIONS } from '../../hooks/usePermissions';
import { useColorMode } from '../../hooks/useColorMode';
import Typography from '@mui/material/Typography';
import { getEnterpriseIdFromToken } from '../context/EnterpriseConfigContext';

export default function AccountMenu() {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const theme = useTheme();
    const { mode: currentTheme, toggleMode: toggleTheme } = useColorMode();
    const navigate = useNavigate();
    const open = Boolean(anchorEl);
    const { companyLogo, isLoading: logoLoading } = useCompanyLogo();
    const { userData, hasAnyPermission } = usePermissions();
    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    // Função para capitalizar o nome
    const capitalizeName = (name) => {
        if (!name) return '';
        return name.split(' ').map(word => 
            word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        ).join(' ');
    };

    const userName = userData ? (userData.name || userData.username) : '';
    const displayName = capitalizeName(userName);

    return (
        <React.Fragment>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <Box
                    component="button"
                    onClick={toggleTheme}
                    title={currentTheme === 'light' ? 'Alternar para tema escuro' : 'Alternar para tema claro'}
                    sx={{
                        width: 38,
                        height: 38,
                        borderRadius: '10px',
                        border: `1px solid ${theme.palette.divider}`,
                        backgroundColor: 'background.paper',
                        color: 'text.secondary',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        flexShrink: 0,
                        '&:hover': { backgroundColor: 'background.subtle', color: 'text.primary', borderColor: 'primary.main' },
                    }}
                >
                    {currentTheme === 'light' ? <Moon size={17} /> : <Sun size={17} />}
                </Box>

                <Box
                    onClick={handleClick}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: { xs: 0.5, sm: 1.1 },
                        pl: 0.5,
                        pr: { xs: 0.75, sm: 1.25 },
                        py: 0.5,
                        borderRadius: '24px',
                        border: `1px solid ${theme.palette.divider}`,
                        backgroundColor: 'background.paper',
                        cursor: 'pointer',
                        '&:hover': { borderColor: 'primary.main' },
                    }}
                    aria-controls={open ? 'account-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? 'true' : undefined}
                >
                    <Avatar sx={{ width: 30, height: 30, backgroundImage: theme.palette.gradient.brand, fontSize: 12, fontWeight: 700 }}>
                        {logoLoading ? <LinearProgress sx={{ width: 30, height: 30 }} /> : companyLogo ? <img src={companyLogo} alt="Logo da empresa" style={{ width: 30, height: 30 }} /> : <AccountCircleIcon sx={{ fontSize: 30 }} />}
                    </Avatar>
                    {displayName && (
                        <Box sx={{ lineHeight: 1.2, display: { xs: 'none', sm: 'block' } }}>
                            <Typography sx={{ fontSize: '12.5px', fontWeight: 600, color: 'text.primary' }}>
                                {displayName}
                            </Typography>
                            {userData?.profile_name && (
                                <Typography sx={{ fontSize: '10.5px', color: 'text.disabled' }}>
                                    {userData.profile_name}
                                </Typography>
                            )}
                        </Box>
                    )}
                    <ChevronDown size={14} color={theme.palette.text.disabled} />
                </Box>
            </Box>
            <Menu
                anchorEl={anchorEl}
                id="account-menu"
                open={open}
                onClose={handleClose}
                PaperProps={{
                    elevation: 0,
                    sx: {
                        overflow: 'visible',
                        filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                        mt: 1.5,
                        '& .MuiAvatar-root': {
                            width: 32,
                            height: 32,
                            ml: -0.5,
                            mr: 1,
                        },
                        '&:before': {
                            content: '""',
                            display: 'block',
                            position: 'absolute',
                            top: 0,
                            right: 14,
                            width: 10,
                            height: 10,
                            bgcolor: 'background.paper',
                            transform: 'translateY(-50%) rotate(45deg)',
                            zIndex: 0,
                        },
                    },
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                {userData?.is_owner === true && (
                    <MenuItem onClick={() => {
                        handleClose();
                        navigate('/myaccount');
                    }}>
                        <ListItemIcon>
                            <Person fontSize="small" />
                        </ListItemIcon>
                        Minha Conta
                    </MenuItem>
                )}
                {userData?.is_owner === true && (
                    <MenuItem onClick={() => {
                        handleClose();
                        navigate('/audit/logs');
                    }}>
                        <ListItemIcon>
                            <History fontSize="small" />
                        </ListItemIcon>
                        Logs de Atividade
                    </MenuItem>
                )}
                {hasAnyPermission(SETTINGS_PERMISSIONS) && (
                    <MenuItem onClick={() => {
                        handleClose();
                        navigate('/settings');
                    }}>
                        <ListItemIcon>
                            <Settings fontSize="small"  />
                        </ListItemIcon>
                        Configurações
                    </MenuItem>
                )}
                <Divider />
                <MenuItem onClick={() => {
                    handleClose();
                    // Antes de apagar o token (a chave usa o enterprise_id dele).
                    sessionStorage.removeItem('sales_list_filters:' + (getEnterpriseIdFromToken() || 'anon'));
                    sessionStorage.removeItem('service_orders_list_filters:' + (getEnterpriseIdFromToken() || 'anon'));
                    sessionStorage.removeItem('service_quotes_list_filters:' + (getEnterpriseIdFromToken() || 'anon'));
                    sessionStorage.removeItem('products_list_filters:' + (getEnterpriseIdFromToken() || 'anon'));
                    localStorage.removeItem('access_token');
                    localStorage.removeItem('refresh_token');
                    localStorage.removeItem('user_permissions_cache');
                    navigate('/');
                }}>
                    <ListItemIcon>
                        <Logout fontSize="small" />
                    </ListItemIcon>
                    Sair
                </MenuItem>
            </Menu>
        </React.Fragment>
    );
}
